/**
 * Audit Log Filters Component
 *
 * Advanced filtering interface for audit logs with React Hook Form.
 * Filters live in a right-side Sheet (slideout panel) with applied
 * filters shown as compact badges in the main area.
 *
 * When a tab is active (Application Logs or Admin Logs), the category filter
 * is automatically applied based on the tab and hidden from the UI to avoid confusion.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router';
import { Search, X, Calendar as CalendarIcon, Info, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useFilters } from '@/state/filters-context';
import type {
  AuditLogCategory,
  AuditLogFilters,
  AuditLogSeverity,
  AuditLogTab,
  CacheStatusFilter,
} from '@/types/audit-log';
import {
  AUDIT_LOG_CATEGORY_LABELS,
  AUDIT_LOG_SEVERITY_LABELS,
  AUDIT_LOG_TAB_LABELS,
  CACHE_STATUS_LABELS,
  CACHE_STATUS_COLORS,
  getCategoriesForTab,
} from '@/types/audit-log';
import { cn } from '@/lib/utils';

/**
 * Filter Form Schema
 * Validation schema for filter inputs
 */
const filterFormSchema = z.object({
  userId: z.string().optional(),
  category: z.string().optional(),
  severity: z.string().optional(),
  eventType: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  resourceType: z.string().optional(),
  resourceId: z.string().optional(),
  correlationId: z.string().optional(),
  ipAddress: z.string().optional(),
  sortBy: z.enum(['occurredAt', 'severity']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  cacheStatus: z.array(z.enum(['hit', 'miss', 'na'])).optional(),
});

type FilterFormValues = z.infer<typeof filterFormSchema>;

/** Human-readable labels for filter keys */
const FILTER_LABELS: Record<string, string> = {
  userId: 'User ID',
  category: 'Category',
  severity: 'Severity',
  eventType: 'Event Type',
  startDate: 'Start Date',
  endDate: 'End Date',
  resourceType: 'Resource Type',
  resourceId: 'Resource ID',
  correlationId: 'Correlation ID',
  ipAddress: 'IP Address',
  sortBy: 'Sort By',
  sortOrder: 'Sort Order',
  cacheStatus: 'Cache Status',
};

/**
 * Format a filter value for display in a badge.
 */
function formatFilterValue(key: string, value: unknown): string {
  if (key === 'category') {
    if (Array.isArray(value)) {
      return value.map(v => AUDIT_LOG_CATEGORY_LABELS[v as AuditLogCategory] || v).join(', ');
    }
    return AUDIT_LOG_CATEGORY_LABELS[value as AuditLogCategory] || String(value);
  }
  if (key === 'severity') {
    if (Array.isArray(value)) {
      return value.map(v => AUDIT_LOG_SEVERITY_LABELS[v as AuditLogSeverity] || v).join(', ');
    }
    return AUDIT_LOG_SEVERITY_LABELS[value as AuditLogSeverity] || String(value);
  }
  if (key === 'cacheStatus' && Array.isArray(value)) {
    return value.map(v => CACHE_STATUS_LABELS[v] || v).join(', ');
  }
  if (key === 'startDate' || key === 'endDate') {
    return new Date(String(value)).toLocaleDateString();
  }
  if (key === 'sortBy') {
    return value === 'severity' ? 'Severity' : 'Occurred At';
  }
  if (key === 'sortOrder') {
    return value === 'asc' ? 'Ascending' : 'Descending';
  }
  return String(value);
}

/** Keys that are defaults or controlled by tabs and should not show as active badges */
const HIDDEN_FILTER_KEYS = new Set(['page', 'limit']);

/**
 * Audit Log Filters Component
 */
export function AuditLogFilters() {
  const { filters, setFilters, resetFilters } = useFilters();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);

  // Get active tab from URL params (if any)
  const activeTab = (searchParams.get('tab') as AuditLogTab) || 'application';

  // Initialize form with current filter values
  const form = useForm<FilterFormValues>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: {
      userId: filters.userId || '',
      category: Array.isArray(filters.category) ? '_all' : filters.category || '_all',
      severity: Array.isArray(filters.severity) ? '_all' : filters.severity || '_all',
      eventType: filters.eventType || '',
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      resourceType: filters.resourceType || '',
      resourceId: filters.resourceId || '',
      correlationId: filters.correlationId || '',
      ipAddress: filters.ipAddress || '',
      sortBy: filters.sortBy || 'occurredAt',
      sortOrder: filters.sortOrder || 'desc',
      cacheStatus: filters.cacheStatus || [],
    },
  });

  // Get current form values for controlled inputs
  // eslint-disable-next-line react-hooks/incompatible-library
  const watchedValues = form.watch();
  const categoryValue = watchedValues.category;
  const severityValue = watchedValues.severity;
  const sortByValue = watchedValues.sortBy;
  const sortOrderValue = watchedValues.sortOrder;
  const cacheStatusValue = watchedValues.cacheStatus || [];

  /**
   * Handle Form Submit
   * Apply filters, reset to page 1, and close the sheet
   */
  const onSubmit = (values: FilterFormValues) => {
    const newFilters: AuditLogFilters = {
      ...filters,
      page: 1,
      userId: values.userId || undefined,
      category:
        values.category && values.category !== '_all'
          ? (values.category as AuditLogCategory)
          : undefined,
      severity:
        values.severity && values.severity !== '_all'
          ? (values.severity as AuditLogSeverity)
          : undefined,
      eventType: values.eventType || undefined,
      startDate: values.startDate || undefined,
      endDate: values.endDate || undefined,
      resourceType: values.resourceType || undefined,
      resourceId: values.resourceId || undefined,
      correlationId: values.correlationId || undefined,
      ipAddress: values.ipAddress || undefined,
      sortBy: values.sortBy || 'occurredAt',
      sortOrder: values.sortOrder || 'desc',
      cacheStatus:
        values.cacheStatus && values.cacheStatus.length > 0 ? values.cacheStatus : undefined,
    };

    // Preserve tab-controlled category
    if (activeTab) {
      newFilters.category = getCategoriesForTab(activeTab);
    }

    setFilters(newFilters);
    setOpen(false);
  };

  /**
   * Handle Clear Filters
   * Reset form and filters to defaults, then close the sheet
   */
  const handleClear = () => {
    form.reset({
      userId: '',
      category: '_all',
      severity: '_all',
      eventType: '',
      startDate: '',
      endDate: '',
      resourceType: '',
      resourceId: '',
      correlationId: '',
      ipAddress: '',
      sortBy: 'occurredAt',
      sortOrder: 'desc',
      cacheStatus: [],
    });
    const categoriesForTab = activeTab ? getCategoriesForTab(activeTab) : undefined;
    resetFilters(categoriesForTab ? { category: categoriesForTab } : undefined);
    setOpen(false);
  };

  /**
   * Toggle Cache Status Filter
   */
  const toggleCacheStatus = (status: CacheStatusFilter) => {
    const current = cacheStatusValue;
    const newValue = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    form.setValue('cacheStatus', newValue);
  };

  /**
   * Remove a single active filter
   */
  const removeFilter = (key: string) => {
    const newFilters = { ...filters };

    if (key === 'sortBy') {
      newFilters.sortBy = 'occurredAt';
      form.setValue('sortBy', 'occurredAt');
    } else if (key === 'sortOrder') {
      newFilters.sortOrder = 'desc';
      form.setValue('sortOrder', 'desc');
    } else {
      delete newFilters[key as keyof typeof newFilters];
      // Reset form field
      if (key === 'category') form.setValue('category', '_all');
      else if (key === 'severity') form.setValue('severity', '_all');
      else if (key === 'cacheStatus') form.setValue('cacheStatus', []);
      else form.setValue(key as keyof FilterFormValues, '');
    }

    newFilters.page = 1;

    // Preserve tab-controlled category
    if (activeTab) {
      newFilters.category = getCategoriesForTab(activeTab);
    }

    setFilters(newFilters);
  };

  /**
   * Compute active filter badges (skip defaults and tab-controlled values)
   */
  const activeFilters: { key: string; label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (HIDDEN_FILTER_KEYS.has(key)) continue;
    if (value === undefined || value === null || value === '') continue;

    // Skip tab-controlled category (it's an array when set by tab)
    if (key === 'category' && activeTab) continue;

    // Skip default sort values
    if (key === 'sortBy' && value === 'occurredAt') continue;
    if (key === 'sortOrder' && value === 'desc') continue;

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) continue;

    activeFilters.push({
      key,
      label: FILTER_LABELS[key] || key,
      value: formatFilterValue(key, value),
    });
  }

  const activeFilterCount = activeFilters.length;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Applied filter badges */}
      {activeFilters.map(({ key, label, value }) => (
        <Badge key={key} variant="secondary" className="gap-1 pl-2 pr-1 py-1">
          <span className="text-muted-foreground text-xs">{label}:</span>
          <span className="text-xs">{value}</span>
          <button
            type="button"
            onClick={() => removeFilter(key)}
            className="ml-1 rounded-sm hover:bg-muted p-0.5"
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Remove {label} filter</span>
          </button>
        </Badge>
      ))}

      {/* Filters sheet trigger */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </SheetTrigger>

        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>Narrow down audit logs by specific criteria.</SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1 px-6">
            {/* Info Alert when tab is active */}
            {activeTab && (
              <Alert className="mb-4">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Filtering by <strong>{AUDIT_LOG_TAB_LABELS[activeTab]}</strong>. Category filter
                  is applied automatically.
                </AlertDescription>
              </Alert>
            )}

            <form
              id="audit-log-filters-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 pb-6"
            >
              {/* Date Range */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">
                    <CalendarIcon className="inline h-3 w-3 mr-1" />
                    Start Date
                  </Label>
                  <Input
                    id="startDate"
                    type="datetime-local"
                    {...form.register('startDate')}
                    placeholder="Start date"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate">
                    <CalendarIcon className="inline h-3 w-3 mr-1" />
                    End Date
                  </Label>
                  <Input
                    id="endDate"
                    type="datetime-local"
                    {...form.register('endDate')}
                    placeholder="End date"
                  />
                </div>
              </div>

              {/* Category and Severity */}
              <div className="grid grid-cols-1 gap-4">
                {/* Hide category filter when tab is active */}
                {!activeTab && (
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={categoryValue || '_all'}
                      onValueChange={value => form.setValue('category', value)}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="All categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">All categories</SelectItem>
                        {Object.entries(AUDIT_LOG_CATEGORY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="severity">Severity</Label>
                  <Select
                    value={severityValue || '_all'}
                    onValueChange={value => form.setValue('severity', value)}
                  >
                    <SelectTrigger id="severity">
                      <SelectValue placeholder="All severities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All severities</SelectItem>
                      {Object.entries(AUDIT_LOG_SEVERITY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Event Type and User ID */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="eventType">Event Type</Label>
                  <Input
                    id="eventType"
                    {...form.register('eventType')}
                    placeholder="e.g., user.login, order.created"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userId">User ID</Label>
                  <Input id="userId" {...form.register('userId')} placeholder="Enter user ID" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ipAddress">IP Address</Label>
                  <Input
                    id="ipAddress"
                    {...form.register('ipAddress')}
                    placeholder="e.g., 192.168.1.1"
                  />
                </div>
              </div>

              {/* Resource Type and Resource ID */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="resourceType">Resource Type</Label>
                  <Input
                    id="resourceType"
                    {...form.register('resourceType')}
                    placeholder="e.g., user, order, basket"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resourceId">Resource ID</Label>
                  <Input
                    id="resourceId"
                    {...form.register('resourceId')}
                    placeholder="Enter resource ID"
                  />
                </div>
              </div>

              {/* Correlation ID */}
              <div className="space-y-2">
                <Label htmlFor="correlationId">Correlation ID</Label>
                <Input
                  id="correlationId"
                  {...form.register('correlationId')}
                  placeholder="Enter correlation ID to trace related events"
                />
              </div>

              {/* Cache Status Filter */}
              <div className="space-y-2">
                <Label>Cache Status</Label>
                <div className="flex flex-wrap gap-2">
                  {(['hit', 'miss', 'na'] as CacheStatusFilter[]).map(status => (
                    <Button
                      key={status}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => toggleCacheStatus(status)}
                      className={cn(
                        'transition-all',
                        cacheStatusValue.includes(status)
                          ? cn(CACHE_STATUS_COLORS[status], 'border-2 border-current')
                          : 'opacity-60 hover:opacity-100'
                      )}
                    >
                      {CACHE_STATUS_LABELS[status]}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Select cache statuses to filter (multiple allowed)
                </p>
              </div>

              {/* Sort Controls */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sortBy">Sort By</Label>
                  <Select
                    value={sortByValue || 'occurredAt'}
                    onValueChange={value =>
                      form.setValue('sortBy', value as 'occurredAt' | 'severity')
                    }
                  >
                    <SelectTrigger id="sortBy">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="occurredAt">Occurred At</SelectItem>
                      <SelectItem value="severity">Severity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sortOrder">Sort Order</Label>
                  <Select
                    value={sortOrderValue || 'desc'}
                    onValueChange={value => form.setValue('sortOrder', value as 'asc' | 'desc')}
                  >
                    <SelectTrigger id="sortOrder">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Descending (Newest First)</SelectItem>
                      <SelectItem value="asc">Ascending (Oldest First)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </form>
          </ScrollArea>

          {/* Action Buttons - pinned at bottom */}
          <div className="flex gap-2 px-6 py-4 border-t">
            <Button type="submit" form="audit-log-filters-form" className="flex-1">
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
            <Button type="button" variant="outline" onClick={handleClear}>
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
